import { useQueryClient } from '@tanstack/react-query';
import { type SourcingChannels } from 'api-clients/monolith';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';
import { useAuthContext } from '@/contexts/auth-context';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { usePostHog } from '@/hooks/use-posthog';
import { queryKeys } from '@/query-keys';
import { isSourceFreemium } from '@/utils/is-source-freemium';
import { SOURCING_CURRENCY, SOURCING_PAY_TYPE } from '@/utils/sourcing-billing';

import { findCachedOpening } from '../job-details/hooks/cached-opening';
import { useUpdateOpeningDescription } from '../job-details/hooks/use-update-opening-description';
import {
  buildOpeningUpdateBody,
  hasUnsavedDetails,
} from '../job-details/opening-update-body';
import { useValidatePostingRequirements } from '../job-details/hooks/use-validate-posting-requirements';
import { usePoolForm } from '../pool-setup/use-pool-form';
import { JobPostingStep } from '../types';
import { openingIdsInChannels } from '../utils/channel-utils';
import {
  confirmedDetailFields,
  countTopPicksSelected,
  isMarketplaceBoardSelected,
} from '../utils/job-post-analytics';
import { getNextStep, JOB_POSTING_STEP_ORDER } from './step-config';

export function useValidatedStepper() {
  const intl = useIntl();
  const { notifyWarning } = useToaster();
  const { whoami } = useAuthContext();
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const {
    currentStep,
    setCurrentStep,
    hookForm,
    selectedChannelSlugs,
    selectedOpeningIds,
    openings,
    markStepCompleted,
    confirmOpeningSelection,
    openFreemiumMultiOpeningDialog,
    getConfirmedDetailSaves,
  } = useJobPostingContext();

  // Tracks time_on_step_seconds — reset whenever the visible step changes.
  const stepEnteredAtRef = useRef<number>(Date.now());
  useEffect(() => {
    stepEnteredAtRef.current = Date.now();
  }, [currentStep]);
  const timeOnStepSeconds = () =>
    Math.round((Date.now() - stepEnteredAtRef.current) / 1000);

  // handleStepClick replays validateStep for every step it jumps over, and
  // Back then Next revalidates a finished step, so a step's *_complete event
  // is sent the first time the step completes and never again.
  const sentStepEventsRef = useRef(new Set<JobPostingStep>());
  const isFirstStepEvent = (step: JobPostingStep) => {
    if (sentStepEventsRef.current.has(step)) return false;
    sentStepEventsRef.current.add(step);
    return true;
  };

  const { updateOpeningDescriptionAsync, isUpdatingOpening } =
    useUpdateOpeningDescription();
  const {
    validateAllContractsForOpeningAsync,
    isValidatingPostingRequirements,
  } = useValidatePostingRequirements();
  const { validateOverlappingPoolPurchasesAsync, isValidatingPoolPurchases } =
    usePoolForm();

  const {
    trigger,
    getValues,
    setValue,
    formState: { errors },
  } = hookForm;

  const validateStep = async (step: JobPostingStep) => {
    if (step === 'select-openings') {
      if (selectedOpeningIds.length === 0) {
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage: 'Select at least one opening to continue',
            description:
              'Warning when the user tries to leave the select-openings step with no openings checked',
          }),
        });
        return false;
      }

      if (isSourceFreemium(whoami) && selectedOpeningIds.length > 1) {
        openFreemiumMultiOpeningDialog();
        return false;
      }

      confirmOpeningSelection();

      if (isFirstStepEvent('select-openings')) {
        posthog.capture('source_job_post:opening_selection_complete', {
          openings_selected: selectedOpeningIds.map(
            id => openings?.[id]?.title ?? id,
          ),
          openings_id: selectedOpeningIds,
        });
      }
    }

    /** Validate channels step */
    if (step === 'select-channels') {
      const isValid = selectedChannelSlugs.length > 0;

      if (!isValid) {
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage: 'Please select at least one channel to proceed',
            description: 'Error message for no channels selected',
          }),
        });
        return false;
      }

      const channels = getValues('channels') ?? {};
      const topPicks = queryClient
        .getQueriesData<SourcingChannels>({
          queryKey: queryKeys.jobPosting.channels.popular,
        })
        .flatMap(([, data]) => (data ? [data.popular_channels] : []));
      if (isFirstStepEvent('select-channels')) {
        posthog.capture('source_job_post:job_board_complete', {
          channels_selected: selectedChannelSlugs,
          product_selected: Object.keys(channels),
          channels_selected_count: selectedChannelSlugs.length,
          top_picks_selected_count: countTopPicksSelected(channels, topPicks),
          is_marketplace_board_selected: isMarketplaceBoardSelected(channels),
          openings_selected: selectedOpeningIds.map(
            id => openings?.[id]?.title ?? id,
          ),
          openings_id: selectedOpeningIds,
        });
      }
    }

    /** Validate job details step */
    if (step === 'job-details') {
      const isValid = await trigger('jobDetails');

      if (!isValid) {
        const errorCount = Object.keys(errors?.jobDetails ?? {}).length ?? 0;

        notifyWarning({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, =1 {You have one incomplete job. Please fill it in to continue.} other {You have {count} incomplete jobs. Please fill them in to continue.}}',
              description: 'Error message for incomplete job descriptions',
            },
            { count: errorCount },
          ),
        });
        return false;
      }

      // A single opening saves on Next. A multi-opening posting saves each
      // opening from its own card, and Next saves the ones whose edits the
      // recruiter typed but never saved, so Hire holds what the step validated.
      // An opening removed from every channel has no card and nothing to post,
      // so it never saves. Each save validates the opening's Vonq contract
      // fields server-side first.
      const inChannels = openingIdsInChannels(getValues('channels'));
      const openingsToSave = selectedOpeningIds.filter(openingExternalId => {
        if (!inChannels.has(openingExternalId)) return false;
        const detail = getValues(`jobDetails.${openingExternalId}`);
        if (detail === undefined) return false;
        if (selectedOpeningIds.length === 1) return true;
        const saved = findCachedOpening(queryClient, openingExternalId);
        return saved !== undefined && hasUnsavedDetails(detail, saved);
      });

      // Every body is built before the first save: a confirmed save writes
      // its address into the cached openings on the same Location, and a
      // sibling's body built after it would send its untouched copy back.
      const saves = openingsToSave.map(openingExternalId => ({
        openingExternalId,
        requestBody: buildOpeningUpdateBody(
          getValues(`jobDetails.${openingExternalId}`),
          findCachedOpening(queryClient, openingExternalId),
        ),
      }));

      for (const { openingExternalId, requestBody } of saves) {
        const isServerValid =
          await validateAllContractsForOpeningAsync(openingExternalId);

        if (!isServerValid) {
          notifyWarning({
            message: intl.formatMessage({
              defaultMessage:
                'Some posting requirement fields have errors. Please review and correct them.',
              description:
                'Warning shown when server-side posting requirements validation finds errors during stepper navigation',
            }),
          });
          return false;
        }

        try {
          await updateOpeningDescriptionAsync({
            openingExternalId,
            requestBody,
          });
        } catch {
          // Error is already handled by the mutation's onError callback
          return false;
        }
      }

      // The step counts as complete only once every selected opening has a
      // server-confirmed save; details_added lists the fields those saves
      // changed from each opening's first-save baseline.
      const confirmedSaves = getConfirmedDetailSaves();
      if (
        selectedOpeningIds.length > 0 &&
        selectedOpeningIds.every(id => id in confirmedSaves) &&
        isFirstStepEvent('job-details')
      ) {
        posthog.capture('source_job_post:opening_details_complete', {
          opening_name: selectedOpeningIds.map(
            id => openings?.[id]?.title ?? id,
          ),
          opening_id: selectedOpeningIds,
          details_added: confirmedDetailFields(
            confirmedSaves,
            selectedOpeningIds,
          ),
          time_on_step_seconds: timeOnStepSeconds(),
        });
      }
    }

    /** Validate pool step */
    if (step === 'pool-setup') {
      const isValid = await trigger('channels.pool');

      if (!isValid) {
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage: 'Please complete the pool setup to proceed',
            description: 'Error message for incomplete pool setup',
          }),
        });
        return false;
      }

      const noConflicts = await validateOverlappingPoolPurchasesAsync();
      if (noConflicts) {
        // Mark the pool channel entry as validated so campaign-details step
        // can confirm all channels have been saved/validated.
        setValue('channels.pool.isValidated', true);
      }
      return noConflicts;
    }

    /** Validate channel forms step */
    if (step === 'campaign-details') {
      const isValid = await trigger('channels');

      if (!isValid) {
        /** Validate channel form errors */
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage:
              'Please complete the spend details to proceed to payment.',
            description: 'Error message for incomplete spend details',
          }),
        });
        return false;
      }

      const channels = getValues('channels');

      /** Validate if at least one channel is selected */
      if (Object.keys(channels).length === 0) {
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage: 'Please select at least one channel to proceed',
            description: 'Error message for no channels selected',
          }),
        });
        return false;
      }

      /** Validate if channel forms are saved and validated */
      const isAllChannelsValidated = Object.values(channels).every(
        channel => channel.isValidated,
      );

      if (!isAllChannelsValidated) {
        notifyWarning({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, =1 {Please save the channel form before proceeding.} other {Please save all channel forms before proceeding.}}',
              description: 'Error message for incomplete channel forms',
            },
            { count: selectedChannelSlugs.length },
          ),
        });
        return false;
      }

      // Every channel here is already individually server-confirmed — saving
      // each channel form is what sets its own `isValidated` flag above.
      const finalBudgetAllocated = Object.values(channels).reduce(
        (sum, channel) => sum + (channel.totalChannelBudget ?? 0),
        0,
      );
      if (isFirstStepEvent('campaign-details')) {
        posthog.capture('source_job_post:budget_details_complete', {
          final_budget_allocated: finalBudgetAllocated,
          currency: SOURCING_CURRENCY,
          pay_type: SOURCING_PAY_TYPE,
          time_on_step_seconds: timeOnStepSeconds(),
        });
      }
    }

    return true;
  };

  /* Handle next step button click */
  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (!isValid) return;

    markStepCompleted(currentStep);
    const nextStep = getNextStep(currentStep, selectedChannelSlugs);
    if (nextStep !== null) setCurrentStep(nextStep);
  };

  /* Handle when users navigate to a step directly from the stepper */
  const handleStepClick = async (toStep: JobPostingStep) => {
    if (JOB_POSTING_STEP_ORDER[toStep] <= JOB_POSTING_STEP_ORDER[currentStep]) {
      setCurrentStep(toStep);
      return;
    }

    // Sequential validation is intentional - we validate steps in order and
    // stop at the first invalid step so the user sees a single error message.
    let step = currentStep;
    /* eslint-disable no-await-in-loop */
    while (step !== toStep) {
      const isValid = await validateStep(step);
      if (!isValid) {
        setCurrentStep(step);
        return;
      }

      markStepCompleted(step);
      const next = getNextStep(step, selectedChannelSlugs);
      // `next` being null or past `toStep` means `toStep` is unreachable from
      // the current configuration (e.g. it was filtered out by isStepVisible).
      // The breadcrumb hides such steps, so this is purely defensive.
      if (
        next === null ||
        JOB_POSTING_STEP_ORDER[next] > JOB_POSTING_STEP_ORDER[toStep]
      ) {
        setCurrentStep(step);
        return;
      }

      step = next;
    }
    /* eslint-enable no-await-in-loop */

    setCurrentStep(toStep);
  };

  return {
    handleNext,
    handleStepClick,
    validateStep,
    isLoading:
      isUpdatingOpening ||
      isValidatingPoolPurchases ||
      isValidatingPostingRequirements,
  };
}
